import { usePreferencesStore } from '../stores';
import { ApiService } from './ApiService';
import { AuthService } from './AuthService';

interface TranslationModel {
	[key: string]: string;
}

interface UserLanguagePreference {
	languageCode: string;
}

export class LanguageService {
	private static instance: LanguageService;
	private languageCode: string | null = null;
	private readonly apiService: typeof ApiService;
	private readonly authService: typeof AuthService;

	private constructor() {
		this.apiService = ApiService;
		this.authService = AuthService.getInstance();
		this.loadStoredLanguage();
	}

	public static getInstance(): LanguageService {
		if (!LanguageService.instance) {
			LanguageService.instance = new LanguageService();
		}

		return LanguageService.instance;
	}

	private loadStoredLanguage(): void {
		const stored = usePreferencesStore.getState().language;

		if (stored != null) {
			this.languageCode = stored;
		}
	}

	public async getPreferredLanguage(): Promise<string> {
		// First check if we have a stored language preference
		if (this.languageCode != null) {
			return this.languageCode;
		}

		// Check if user is authenticated and try to fetch their language preference
		const authState = this.authService.getAuthState();
		if (authState.isAuthenticated && authState.user != null) {
			try {
				const userLanguage = await this.fetchUserLanguagePreference();
				if (userLanguage != null) {
					this.setPreferredLanguage(userLanguage);

					return userLanguage;
				}
			} catch (error) {
				// // Error: Failed to fetch user language preference
			}
		}

		// Fall back to browser language
		const browserLanguage = this.detectBrowserLanguage();
		this.setPreferredLanguage(browserLanguage);

		return browserLanguage;
	}

	private async fetchUserLanguagePreference(): Promise<string | null> {
		try {
			const response = await this.apiService.apiGet<UserLanguagePreference>(
				'/language/user/preference',
			);

			return response.languageCode ?? null;
		} catch (error) {
			// // Error: Failed to fetch user language preference

			return null;
		}
	}

	private detectBrowserLanguage(): string {
		let fullLanguage: string;

		if (navigator.languages != null && navigator.languages.length > 0) {
			fullLanguage = navigator.languages[0];
		} else if (navigator.language != null) {
			fullLanguage = navigator.language;
		} else {
			return 'en';
		}

		// Extract just the language code (e.g., 'en' from 'en-US')
		const languageCode = fullLanguage.split('-')[0];

		return languageCode;
	}

	public setPreferredLanguage(languageCode: string): void {
		this.languageCode = languageCode;
		usePreferencesStore.getState().setLanguage(languageCode);
	}

	public async fetchTranslations(
		languageCode?: string,
		bustCache = false,
	): Promise<TranslationModel> {
		const code = languageCode ?? (await this.getPreferredLanguage());

		try {
			// Add cache-busting parameter when explicitly requested
			const url = bustCache
				? `/language/code/${code}?t=${Date.now()}`
				: `/language/code/${code}`;

			const response = await this.apiService.apiGet<{
				translations: TranslationModel;
			}>(url);

			return response.translations;
		} catch (error) {
			// // Error: Failed to fetch translations
			throw error;
		}
	}

	public clearPreferredLanguage(): void {
		this.languageCode = null;
		usePreferencesStore.getState().setLanguage(null);
	}

	public async setLanguage(languageCode: string): Promise<void> {
		this.setPreferredLanguage(languageCode);
		// Here you can add additional logic to apply the language
		// For example, updating the UI, loading translations, etc.
		// This method is async to allow for future enhancements
	}
}
